/**
 * Printer Service
 * Handles printer connectivity for WiFi, Ethernet, and USB printers
 * Supports direct printing without browser print dialogs
 */

class PrinterService {
  constructor(axios) {
    this.axios = axios;
    this.activeConnections = new Map();
    this.usbDevice = null;
    this.printerProfiles = new Map();
    this.isDiscovering = false; // Flag to indicate discovery mode
    this.originalConsoleError = null; // Store original console.error
    this.originalConsoleWarn = null; // Store original console.warn
    this.initializePrinterProfiles();
  }

  /**
   * Suppress console errors during discovery mode
   */
  suppressConsoleErrors() {
    if (!this.isDiscovering) {
      this.originalConsoleError = console.error;
      this.originalConsoleWarn = console.warn;
      this.isDiscovering = true;

      // Suppress WebSocket-related console errors and warnings
      const shouldSuppress = (message) => {
        const lowerMessage = message.toLowerCase();
        return (
          lowerMessage.includes("websocket") ||
          lowerMessage.includes("ws://") ||
          lowerMessage.includes("connection to 'ws://") ||
          lowerMessage.includes("failed: websocket is closed") ||
          lowerMessage.includes("websocket is closed before") ||
          lowerMessage.includes("connection to 'ws:") ||
          message.includes("WebSocket connection to")
        );
      };

      console.error = (...args) => {
        const message = args.join(" ");
        // Only suppress WebSocket errors, keep other errors visible
        if (!shouldSuppress(message)) {
          this.originalConsoleError.apply(console, args);
        }
        // Silently ignore WebSocket errors during discovery
      };

      // Also suppress WebSocket warnings (some browsers log as warnings)
      console.warn = (...args) => {
        const message = args.join(" ");
        // Only suppress WebSocket warnings, keep other warnings visible
        if (!shouldSuppress(message)) {
          this.originalConsoleWarn.apply(console, args);
        }
        // Silently ignore WebSocket warnings during discovery
      };
    }
  }

  /**
   * Restore console errors after discovery
   */
  restoreConsoleErrors() {
    if (this.isDiscovering) {
      if (this.originalConsoleError) {
        console.error = this.originalConsoleError;
        this.originalConsoleError = null;
      }
      if (this.originalConsoleWarn) {
        console.warn = this.originalConsoleWarn;
        this.originalConsoleWarn = null;
      }
      this.isDiscovering = false;
    }
  }

  async testNetworkConnection(printer) {
    try {
      const { ip_address, port } = printer;

      // Create a WebSocket connection for testing
      const testUrl = `ws://${ip_address}:${port}`;

      // Suppress console errors before creating WebSocket if in discovery mode
      // This helps catch errors that browsers log immediately
      const wasSuppressing = this.isDiscovering;
      if (!wasSuppressing) {
        this.suppressConsoleErrors();
      }

      let socket;
      try {
        socket = new WebSocket(testUrl);
      } catch (wsError) {
        // If WebSocket creation fails, restore console and return error
        if (!wasSuppressing) {
          this.restoreConsoleErrors();
        }
        return {
          success: false,
          message: "Connection failed",
          details: { error: wsError.message || "WebSocket creation failed" },
        };
      }

      return new Promise((resolve) => {
        const timeout = setTimeout(() => {
          socket.close();
          if (!wasSuppressing) {
            this.restoreConsoleErrors();
          }
          resolve({
            success: false,
            message: "Connection timeout",
            details: { error: "timeout" },
          });
        }, 2000); // Reduced timeout for faster discovery

        socket.onopen = () => {
          clearTimeout(timeout);
          socket.close();
          if (!wasSuppressing) {
            this.restoreConsoleErrors();
          }
          resolve({
            success: true,
            message: "Connection successful",
            details: { ip_address, port },
          });
        };

        socket.onerror = (error) => {
          clearTimeout(timeout);
          // Suppress WebSocket errors during discovery - they're expected
          // WebSocket will fail for most IPs during network discovery
          if (!wasSuppressing) {
            this.restoreConsoleErrors();
          }
          resolve({
            success: false,
            message: "Connection failed",
            details: { error: "Connection refused or timeout" },
          });
        };

        socket.onclose = () => {
          clearTimeout(timeout);
          if (!wasSuppressing) {
            this.restoreConsoleErrors();
          }
        };
      });
    } catch (error) {
      return {
        success: false,
        message: "Connection test failed",
        details: { error: error.message },
      };
    }
  }

  /**
   * Initialize printer profiles for different brands
   */
  initializePrinterProfiles() {
    // EPON Profile
    this.printerProfiles.set("epson", {
      name: "EPSON",
      encoding: "cp850",
      init: [0x1b, 0x40], // Initialize
      cut: [0x1d, 0x56, 0x00], // Full cut
      partialCut: [0x1d, 0x56, 0x01], // Partial cut
      boldOn: [0x1b, 0x45, 0x01], // Bold on
      boldOff: [0x1b, 0x45, 0x00], // Bold off
      alignCenter: [0x1b, 0x61, 0x01], // Center align
      alignLeft: [0x1b, 0x61, 0x00], // Left align
      alignRight: [0x1b, 0x61, 0x02], // Right align
      fontSizeNormal: [0x1d, 0x21, 0x00], // Normal font
      fontSizeLarge: [0x1d, 0x21, 0x11], // Large font
      lineHeight: [0x1b, 0x33, 0x16], // Line spacing
    });

    // Generic Thermal Printer Profile
    this.printerProfiles.set("generic", {
      name: "Generic",
      encoding: "utf8",
      init: [0x1b, 0x40],
      cut: [0x1d, 0x56, 0x00],
      partialCut: [0x1d, 0x56, 0x01],
      boldOn: [0x1b, 0x45, 0x01],
      boldOff: [0x1b, 0x45, 0x00],
      alignCenter: [0x1b, 0x61, 0x01],
      alignLeft: [0x1b, 0x61, 0x00],
      alignRight: [0x1b, 0x61, 0x02],
      fontSizeNormal: [0x1d, 0x21, 0x00],
      fontSizeLarge: [0x1d, 0x21, 0x10],
      lineHeight: [0x1b, 0x33, 0x16],
    });

    // Zjiang Profile
    this.printerProfiles.set("zjiang", {
      name: "Zjiang",
      encoding: "gb18030",
      init: [0x1b, 0x40],
      cut: [0x1d, 0x56, 0x00],
      partialCut: [0x1d, 0x56, 0x01],
      boldOn: [0x1b, 0x45, 0x01],
      boldOff: [0x1b, 0x45, 0x00],
      alignCenter: [0x1b, 0x61, 0x01],
      alignLeft: [0x1b, 0x61, 0x00],
      alignRight: [0x1b, 0x61, 0x02],
      fontSizeNormal: [0x1d, 0x21, 0x00],
      fontSizeLarge: [0x1d, 0x21, 0x11],
      lineHeight: [0x1b, 0x33, 0x16],
    });
  }

  /**
   * Get printer profile based on printer brand or generic
   * @param {string} brand - Printer brand name
   * @returns {Object} Printer profile
   */
  getPrinterProfile(brand = "generic") {
    const brandLower = brand.toLowerCase();
    return (
      this.printerProfiles.get(brandLower) ||
      this.printerProfiles.get("generic")
    );
  }

  /**
   * Test printer connection based on type
   * @param {Object} printer - Printer configuration
   * @returns {Promise<Object>} Connection test result
   */
  async testConnection(printer) {
    switch (printer.type) {
      case "ethernet":
      case "wifi":
        return await this.testNetworkConnection(printer);
      case "usb":
        return await this.testUSBConnection(printer);
      default:
        return {
          success: false,
          message: "Unsupported printer type",
          details: { error: "unsupported_type", type: printer.type },
        };
    }
  }

  /**
   * Send print job to network printer
   * @param {Object} printer - Printer configuration
   * @param {string} content - Print content (ESC/POS commands)
   * @param {Object} options - Print options
   * @returns {Promise<Object>} Print result
   */
  async printToNetworkPrinter(printer, content, options = {}) {
    try {
      const { ip_address, port } = printer;
      const printUrl = `http://${ip_address}:${port}/print`;

      const response = await this.axios.post(
        printUrl,
        {
          content: content,
          options: {
            paper_size: printer.paper_size || "58",
            copies: options.copies || 1,
            cut_paper: options.cut_paper !== false,
            ...options,
          },
        },
        {
          timeout: 30000,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      return {
        success: true,
        message: "Print job sent successfully",
        details: {
          printerId: printer.id,
          jobId: response.data.jobId || Date.now(),
          timestamp: new Date().toISOString(),
        },
      };
    } catch (error) {
      return {
        success: false,
        message: "Failed to send print job",
        details: {
          error: error.message,
          printerId: printer.id,
          timestamp: new Date().toISOString(),
        },
      };
    }
  }

  /**
   * Test USB printer connection with Web USB API
   * @param {Object} printer - Printer configuration
   * @returns {Promise<Object>} Connection test result
   */
  async testUSBConnection(printer) {
    try {
      // Check if Web USB API is available
      if (!navigator.usb) {
        return {
          success: false,
          message: "USB not supported in this browser",
          details: { error: "usb_unsupported" },
        };
      }

      // Request USB device with filters for printers
      // Web USB API requires filters array - each filter must have at least one property
      const scanMethods = [
        {
          filters: [
            // USB class for printers
            { classCode: 0x07, subclassCode: 0x01, protocolCode: 0x01 },
          ],
        },
        {
          filters: [
            // Common USB vendor/product codes for thermal printers
            { vendorId: 0x0483 }, // STMicroelectronics (common in thermal printers)
            { vendorId: 0x1cb0 }, // Zjiang
            { vendorId: 0x04b8 }, // Epson
            { vendorId: 0x04dd }, // Sharp
          ],
        },
      ];

      let device = null;

      for (const method of scanMethods) {
        try {
          device = await navigator.usb.requestDevice(method);
          if (device) break;
        } catch (filterError) {
          console.log("USB filter failed, trying next:", filterError.message);
          continue;
        }
      }

      if (!device) {
        return {
          success: false,
          message: "No USB device selected",
          details: { error: "no_device_selected" },
        };
      }

      // Store device for future use
      this.usbDevice = device;

      // Test connection by trying to open device
      try {
        await device.open();
        await device.close();

        return {
          success: true,
          message: "USB device connected successfully",
          details: {
            deviceName: device.productName || "Unknown USB Printer",
            deviceId: device.serialNumber || device.deviceId,
            vendorId: device.vendorId,
            productId: device.productId,
            manufacturer: device.manufacturerName,
          },
        };
      } catch (connectError) {
        return {
          success: false,
          message: "USB device found but connection failed",
          details: {
            error: connectError.message,
            deviceName: device.productName,
            deviceId: device.serialNumber,
          },
        };
      }
    } catch (error) {
      return {
        success: false,
        message: "USB connection failed",
        details: {
          error: error.message,
          code: error.code,
          name: error.name,
        },
      };
    }
  }

  /**
   * Send print job to USB printer with Web USB API
   * @param {Object} printer - Printer configuration
   * @param {string} content - ESC/POS print commands
   * @param {Object} options - Print options
   * @returns {Promise<Object>} Print result
   */
  async printToUSBPrinter(printer, content, options = {}) {
    try {
      if (!navigator.usb) {
        throw new Error("USB not supported in this browser");
      }

      // Get stored device or request new one
      let device = this.usbDevice;
      if (
        !device ||
        device.vendorId !== printer.usb_vendor_id ||
        device.productId !== printer.usb_product_id
      ) {
        const connectionResult = await this.testUSBConnection(printer);
        if (!connectionResult.success) {
          throw new Error(connectionResult.message);
        }
        device = this.usbDevice;
      }

      await device.open();

      try {
        // Find the appropriate interface for printing
        // Interface class 0x07 is for printers
        let printingInterface = null;

        for (const iface of device.configuration.interfaces) {
          if (iface.alternates[0].interfaceClass === 0x07) {
            // Printer class
            printingInterface = iface.interfaceNumber;
            break;
          }
        }

        if (printingInterface === null) {
          // Try to find interface with bulk endpoints
          for (const iface of device.configuration.interfaces) {
            for (const alt of iface.alternates) {
              const hasBulkIn = alt.endpoints.some(
                (ep) => ep.direction === "in" && ep.type === "bulk"
              );
              const hasBulkOut = alt.endpoints.some(
                (ep) => ep.direction === "out" && ep.type === "bulk"
              );
              if (hasBulkIn && hasBulkOut) {
                printingInterface = iface.interfaceNumber;
                break;
              }
            }
            if (printingInterface !== null) break;
          }
        }

        if (printingInterface === null) {
          throw new Error("No suitable USB printing interface found");
        }

        // Claim the interface
        await device.claimInterface(printingInterface);

        // Find the bulk OUT endpoint for sending data
        let outEndpoint = null;
        for (const endpoint of device.configuration.interfaces[
          printingInterface
        ].alternates[0].endpoints) {
          if (endpoint.direction === "out" && endpoint.type === "bulk") {
            outEndpoint = endpoint.endpointNumber;
            break;
          }
        }

        if (!outEndpoint) {
          throw new Error("No suitable USB OUT endpoint found");
        }

        // Convert content to bytes and send in chunks
        let contentBytes;

        // Handle different content types
        if (typeof content === "string") {
          // If content is a string, encode it properly
          contentBytes = new TextEncoder().encode(content);
        } else if (content instanceof Uint8Array) {
          // If content is already a Uint8Array, use it directly
          contentBytes = content;
        } else {
          // Convert to string first, then encode
          contentBytes = new TextEncoder().encode(String(content));
        }

        const chunkSize = 64; // USB chunk size

        for (let i = 0; i < contentBytes.length; i += chunkSize) {
          const chunk = contentBytes.slice(i, i + chunkSize);
          await device.transferOut(outEndpoint, chunk);

          // Small delay between chunks
          if (i + chunkSize < contentBytes.length) {
            await new Promise((resolve) => setTimeout(resolve, 5));
          }
        }

        await device.close();

        return {
          success: true,
          message: "Print job sent via USB successfully",
          details: {
            printerId: printer.id,
            jobId: `usb_${Date.now()}`,
            bytesSent: contentBytes.length,
            interface: printingInterface,
            timestamp: new Date().toISOString(),
          },
        };
      } catch (usbError) {
        try {
          await device.close();
        } catch (closeError) {
          // Ignore close errors
        }
        throw usbError;
      }
    } catch (error) {
      return {
        success: false,
        message: "USB print failed",
        details: {
          error: error.message,
          printerId: printer.id,
          timestamp: new Date().toISOString(),
        },
      };
    }
  }

  /**
   * Send print job to printer
   * @param {Object} printer - Printer configuration
   * @param {Object} printJob - Print job data
   * @returns {Promise<Object>} Print result
   */
  async print(printer, printJob) {
    const { content, options } = printJob;

    switch (printer.type) {
      case "ethernet":
      case "wifi":
        return await this.printToNetworkPrinter(printer, content, options);
      case "usb":
        return await this.printToUSBPrinter(printer, content, options);
      default:
        return {
          success: false,
          message: "Unsupported printer type",
          details: { error: "unsupported_type", type: printer.type },
        };
    }
  }

  /**
   * Discover network printers on local network
   * @returns {Promise<Array>} Discovered printers
   */
  async discoverNetworkPrinters() {
    const discoveredPrinters = [];

    // Suppress console errors during discovery
    this.suppressConsoleErrors();

    try {
      // Common printer ports to scan
      const commonPorts = [9100, 9101, 9102, 8080, 8000];
      const localIPRanges = ["192.168.1", "192.168.0", "10.0.0", "172.16.0"];

      // This is a simplified discovery process
      // In production, you'd implement proper network scanning
      // Limit scanning to avoid too many connection attempts
      const maxIPsPerRange = 50; // Limit to first 50 IPs per range for performance

      for (const range of localIPRanges) {
        for (let i = 1; i <= Math.min(254, maxIPsPerRange); i++) {
          const ip = `${range}.${i}`;

          for (const port of commonPorts) {
            try {
              const testResult = await this.testNetworkConnection({
                ip_address: ip,
                port,
              });
              if (testResult.success) {
                discoveredPrinters.push({
                  ip_address: ip,
                  port: port,
                  name: `Discovered Printer ${ip}:${port}`,
                  type: "ethernet",
                  connection_status: "online",
                });
                // Found a printer, can break early if desired
              }
            } catch (error) {
              // Ignore connection errors during discovery - WebSocket failures are expected
              // Errors are suppressed via console.error override
            }
          }
        }
      }
    } catch (error) {
      // Only log unexpected errors (not WebSocket-related)
      if (!error.message || !error.message.includes("WebSocket")) {
        console.error("Network discovery error:", error);
      }
    } finally {
      // Always restore console errors
      this.restoreConsoleErrors();
    }

    return discoveredPrinters;
  }

  /**
   * Scan for USB devices with enhanced discovery
   * NOTE: This method MUST be called in response to a user gesture (button click)
   * @returns {Promise<Array>} Discovered USB devices
   */
  async scanUSBDevices() {
    try {
      if (!navigator.usb) {
        throw new Error("USB not supported");
      }

      const devices = [];

      // Try different USB scan methods
      // Web USB API requires filters array - each filter must have at least one property
      // Start with broader filters, then try specific ones
      const scanMethods = [
        {
          // Try printer class first (most common)
          filters: [
            { classCode: 0x07, subclassCode: 0x01, protocolCode: 0x01 }, // Printer class
          ],
        },
        {
          // Try known thermal printer vendors
          filters: [
            { vendorId: 0x0483 }, // STMicroelectronics
            { vendorId: 0x1cb0 }, // Zjiang
            { vendorId: 0x04b8 }, // Epson
            { vendorId: 0x04dd }, // Sharp
            { vendorId: 0x1546 }, // Prolific (USB to Serial adapters)
            { vendorId: 0x067b }, // Prolific Technology
          ],
        },
      ];

      for (const method of scanMethods) {
        try {
          // This will show a browser permission dialog
          // Must be called directly from user gesture (button click)
          const device = await navigator.usb.requestDevice(method);
          if (
            device &&
            !devices.find((d) => d.serialNumber === device.serialNumber)
          ) {
            devices.push({
              id: device.serialNumber || device.deviceId,
              name: device.productName || "Unknown USB Printer",
              type: "usb",
              connection_status: "discovered",
              vendorId: device.vendorId,
              productId: device.productId,
              manufacturer: device.manufacturerName,
              serialNumber: device.serialNumber,
            });
            // If we found a device, break to avoid multiple dialogs
            break;
          }
        } catch (scanError) {
          // Handle different error types
          if (scanError.name === "NotFoundError") {
            // No devices found - try next method
            if (method.filters[0].classCode === 0x07) {
              // If printer class filter failed, try vendor filters
              continue;
            } else {
              // Both methods failed, return empty
              break;
            }
          } else if (scanError.name === "SecurityError") {
            // Permission denied or user gesture required
            throw new Error(
              "USB scanning requires permission. Please ensure your USB printer is connected and try again."
            );
          } else {
            // Other errors - try next method
            continue;
          }
        }
      }

      return devices;
    } catch (error) {
      // Handle different error types with helpful messages
      if (
        error.name === "SecurityError" ||
        error.message.includes("user gesture")
      ) {
        throw new Error(
          "USB scanning requires a user action. Please click the scan button."
        );
      } else if (error.name === "NotFoundError") {
        // No compatible devices found
        throw new Error(
          "No USB devices found. Please ensure:\n" +
            "1. Your printer is connected via USB\n" +
            "2. The printer is powered on\n" +
            "3. USB drivers are installed\n" +
            "4. Try scanning again"
        );
      }
      console.error("USB scan error:", error);
      throw error;
    }
  }

  /**
   * Generate test print content for any printer
   * @param {Object} printer - Printer configuration
   * @param {string} testType - Type of test ('basic', 'receipt', 'kitchen')
   * @returns {Uint8Array} ESC/POS commands for test print
   */
  generateTestPrint(printer, testType = "basic") {
    const profile = this.getPrinterProfile(
      printer.printer_brand || printer.brand
    );
    const paperWidth = parseInt(printer.paper_size) || 58;
    const maxWidth = Math.floor((paperWidth - 8) / 2);

    let commands = [];

    // Helper function to convert string commands to Uint8Array using proper encoding for thermal printers
    const stringToBytes = (str) => {
      // Use TextEncoder for proper UTF-8 encoding
      // This preserves text readability while ensuring proper encoding
      const encoder = new TextEncoder();
      return encoder.encode(str);
    };

    // Helper function to convert ESC/POS command arrays to Uint8Array
    const commandToBytes = (commandArray) => {
      return new Uint8Array(commandArray);
    };

    // Initialize printer with ESC/POS commands
    commands.push(...commandToBytes(profile.init));

    // Test print header
    commands.push(...commandToBytes(profile.alignCenter));
    commands.push(...commandToBytes(profile.boldOn));
    commands.push(
      ...stringToBytes(this.centerText("PRINTER TEST", maxWidth) + "\n")
    );
    commands.push(...commandToBytes(profile.boldOff));
    commands.push(
      ...stringToBytes(this.centerText("================", maxWidth) + "\n\n")
    );

    // Printer info
    commands.push(...commandToBytes(profile.alignLeft));
    commands.push(...stringToBytes(`Printer Name: ${printer.name}\n`));
    commands.push(
      ...stringToBytes(`Printer Type: ${printer.type.toUpperCase()}\n`)
    );
    commands.push(...stringToBytes(`Paper Size: ${paperWidth}mm\n`));
    commands.push(
      ...stringToBytes(`Test Date: ${new Date().toLocaleString()}\n`)
    );
    commands.push(...stringToBytes("--------------------------------\n\n"));

    // Different test content based on type
    if (testType === "receipt") {
      commands.push(...commandToBytes(profile.boldOn));
      commands.push(...stringToBytes("RECEIPT TEST:\n"));
      commands.push(...commandToBytes(profile.boldOff));
      commands.push(...stringToBytes("Item 1           $10.00\n"));
      commands.push(...stringToBytes("Item 2           $15.00\n"));
      commands.push(...stringToBytes("Item 3           $5.00\n"));
      commands.push(...stringToBytes("--------------------------------\n"));
      commands.push(...commandToBytes(profile.boldOn));
      commands.push(...stringToBytes("TOTAL:          $30.00\n"));
      commands.push(...commandToBytes(profile.boldOff));
    } else if (testType === "kitchen") {
      commands.push(...commandToBytes(profile.boldOn));
      commands.push(...stringToBytes("KITCHEN ORDER TEST:\n"));
      commands.push(...commandToBytes(profile.boldOff));
      commands.push(...stringToBytes("Table: T01\n"));
      commands.push(...stringToBytes("Order: #001\n"));
      commands.push(...stringToBytes("--------------------------------\n"));
      commands.push(...stringToBytes("1x Burger (No pickles)\n"));
      commands.push(...stringToBytes("2x Fries\n"));
      commands.push(...stringToBytes("1x Coke\n"));
    } else {
      // Basic test
      commands.push(...commandToBytes(profile.fontSizeLarge));
      commands.push(...stringToBytes("Font Size: LARGE\n"));
      commands.push(...commandToBytes(profile.fontSizeNormal));
      commands.push(...stringToBytes("Font Size: Normal\n"));
      commands.push(...stringToBytes("--------------------------------\n"));
      commands.push(...commandToBytes(profile.alignCenter));
      commands.push(...stringToBytes("Centered Text\n"));
      commands.push(...commandToBytes(profile.alignLeft));
      commands.push(...stringToBytes("Left Aligned\n"));
      commands.push(...commandToBytes(profile.alignRight));
      commands.push(...stringToBytes("Right Aligned\n"));
      commands.push(...commandToBytes(profile.alignLeft));
    }

    // Test different formatting
    commands.push(...stringToBytes("\n\n================================\n"));
    commands.push(...stringToBytes("CHARACTER TEST:\n"));
    commands.push(...stringToBytes("ABCDEFGHIJKLMNOPQRSTUVWXYZ\n"));
    commands.push(...stringToBytes("abcdefghijklmnopqrstuvwxyz\n"));
    commands.push(...stringToBytes("0123456789\n"));
    commands.push(...stringToBytes("!@#$%^&*()_+-=[]{}\n"));
    commands.push(...stringToBytes("Lao: ກຂຄຆງຈ\n"));
    commands.push(...stringToBytes("Thai: กขคงจ\n"));
    commands.push(...stringToBytes("================================\n\n"));

    // Footer
    commands.push(...commandToBytes(profile.alignCenter));
    commands.push(
      ...stringToBytes(this.centerText("TEST COMPLETED", maxWidth) + "\n")
    );
    commands.push(
      ...stringToBytes(this.centerText("Check print quality", maxWidth) + "\n")
    );

    // Cut paper
    commands.push(...commandToBytes(profile.cut));

    return new Uint8Array(commands);
  }

  /**
   * Print test page for a specific printer
   * @param {Object} printer - Printer configuration
   * @param {string} testType - Type of test to run
   * @returns {Promise<Object>} Test print result
   */
  async printTestPage(printer, testType = "basic") {
    try {
      const testContent = this.generateTestPrint(printer, testType);

      const result = await this.print(printer, {
        content: testContent,
        options: {
          test: true,
          type: testType,
        },
      });

      if (result.success) {
        return {
          success: true,
          message: "Test print sent successfully",
          details: {
            ...result.details,
            testType,
            printerName: printer.name,
          },
        };
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      return {
        success: false,
        message: "Test print failed",
        details: {
          error: error.message,
          printerId: printer.id,
          testType,
        },
      };
    }
  }

  /**
   * Simple English text test for printer
   * @param {Object} printer - Printer configuration
   * @returns {Promise<Object>} Test print result
   */
  async printEnglishTest(printer) {
    try {
      const profile = this.getPrinterProfile(
        printer.printer_brand || printer.brand
      );

      // Create simple English test content
      const testLines = [
        "ENGLISH TEST PRINT",
        "==================",
        "Hello World!",
        "This is readable text.",
        "Printer Name: " + printer.name,
        "Date: " + new Date().toLocaleDateString(),
        "Time: " + new Date().toLocaleTimeString(),
        "------------------",
        "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
        "abcdefghijklmnopqrstuvwxyz",
        "0123456789",
        "!@#$%^&*()_+-=[]{}",
        "Test completed!",
        "==================",
      ];

      // Build ESC/POS commands
      const commands = [];

      // Helper functions
      const stringToBytes = (str) => {
        const encoder = new TextEncoder();
        return encoder.encode(str);
      };

      const commandToBytes = (commandArray) => {
        return new Uint8Array(commandArray);
      };

      // Initialize printer
      commands.push(...commandToBytes(profile.init));

      // Center align header
      commands.push(...commandToBytes(profile.alignCenter));
      commands.push(...commandToBytes(profile.boldOn));
      commands.push(...stringToBytes(testLines[0] + "\n"));
      commands.push(...commandToBytes(profile.boldOff));
      commands.push(...stringToBytes(testLines[1] + "\n\n"));

      // Left align for content
      commands.push(...commandToBytes(profile.alignLeft));

      // Print each line
      for (let i = 2; i < testLines.length; i++) {
        commands.push(...stringToBytes(testLines[i] + "\n"));
      }

      // Cut paper
      commands.push(...commandToBytes(profile.cut));

      // Convert to single Uint8Array
      const content = new Uint8Array(commands);

      const result = await this.print(printer, {
        content: content,
        options: {
          test: true,
          type: "english",
        },
      });

      if (result.success) {
        return {
          success: true,
          message: "English test print sent successfully",
          details: {
            ...result.details,
            testType: "english",
            printerName: printer.name,
          },
        };
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      return {
        success: false,
        message: "English test print failed",
        details: {
          error: error.message,
          printerId: printer.id,
          testType: "english",
        },
      };
    }
  }

  /**
   * Convert and print content as PDF via browser print
   * @param {Object} printer - Printer configuration (not used for PDF, but for consistency)
   * @param {string|Object} content - Content to convert to PDF
   * @param {Object} options - PDF options
   * @returns {Promise<Object>} Print result
   */
  async printAsPDF(printer, content, options = {}) {
    try {
      // Dynamically import html2pdf to avoid build issues
      const html2pdf = await import("html2pdf.js");

      // Create HTML content for PDF
      let htmlContent = "";

      if (typeof content === "string") {
        htmlContent = content;
      } else if (content.items && Array.isArray(content.items)) {
        // Handle receipt/order data
        htmlContent = this.generateHTMLContent(content);
      } else {
        htmlContent = JSON.stringify(content, null, 2);
      }

      // PDF configuration
      const pdfOptions = {
        margin: [10, 10, 10, 10],
        filename: `print_${Date.now()}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "mm", format: [58, 200], orientation: "portrait" }, // 58mm thermal paper width
        ...options.pdfOptions,
      };

      // Create and print PDF
      const pdfElement = document.createElement("div");
      pdfElement.innerHTML = htmlContent;
      pdfElement.style.fontFamily = "monospace";
      pdfElement.style.fontSize = "12px";
      pdfElement.style.whiteSpace = "pre-wrap";
      pdfElement.style.padding = "10px";

      // Hide element temporarily
      pdfElement.style.position = "absolute";
      pdfElement.style.left = "-9999px";
      document.body.appendChild(pdfElement);

      // Generate PDF and print
      await html2pdf
        .default()
        .from(pdfElement)
        .set(pdfOptions)
        .toPdf()
        .get("pdf")
        .then((pdf) => {
          // Auto-open print dialog
          pdf.autoPrint();
        })
        .output("dataurlnewwindow")
        .then(() => {
          // Clean up
          document.body.removeChild(pdfElement);
        });

      return {
        success: true,
        message: "PDF generated and print dialog opened",
        details: {
          printerId: printer.id,
          timestamp: new Date().toISOString(),
          type: "pdf",
        },
      };
    } catch (error) {
      return {
        success: false,
        message: "PDF generation failed",
        details: {
          error: error.message,
          printerId: printer.id,
          timestamp: new Date().toISOString(),
        },
      };
    }
  }

  /**
   * Generate HTML content from receipt/order data
   * @param {Object} data - Receipt or order data
   * @returns {string} HTML content
   */
  generateHTMLContent(data) {
    const date = new Date().toLocaleString();
    const items = data.items || [];

    let html = `
      <div style="font-family: monospace; font-size: 12px;">
        <div style="text-align: center; font-weight: bold;">
          ${data.restaurantName || "RECEIPT"}
        </div>
        <div style="text-align: center;">
          =================================
        </div>
        <div>Date: ${date}</div>
        <div>Table: ${data.tableName || "N/A"}</div>
        <div>Order: ${data.orderNumber || data.receiptNumber || "N/A"}</div>
        <div>--------------------------------</div>
    `;

    // Add items
    items.forEach((item) => {
      html += `
        <div>
          ${item.quantity}x ${item.name} - $${(
        item.price * item.quantity
      ).toFixed(2)}
          ${
            item.modifications
              ? `<div style="margin-left: 20px; font-size: 10px;">${item.modifications}</div>`
              : ""
          }
        </div>
      `;
    });

    // Add total
    html += `
        <div>--------------------------------</div>
        <div style="font-weight: bold;">TOTAL: $${data.total || "0.00"}</div>
        ${data.paymentMethod ? `<div>Payment: ${data.paymentMethod}</div>` : ""}
        ${data.change ? `<div>Change: $${data.change}</div>` : ""}
        <div style="text-align: center; margin-top: 20px;">
          Thank you for dining!
        </div>
      </div>
    `;

    return html;
  }

  /**
   * Auto-discover and detect all available printers
   * @returns {Promise<Object>} Discovery results
   */
  async autoDiscoverPrinters() {
    const discoveryResults = {
      usb: [],
      network: [],
      errors: [],
      info: [], // Informational messages
    };

    // Discover USB printers
    // NOTE: USB scanning requires direct user gesture, so we skip it in auto-discovery
    // Users should use the dedicated scan button for USB devices
    try {
      if (navigator.usb) {
        // Skip USB discovery in auto mode - requires direct user gesture
        // Users should use the "Scan USB" button instead
        discoveryResults.usb = [];
        discoveryResults.info.push(
          "USB scanning requires a direct user action. Please use the 'Scan USB' button."
        );
      } else {
        discoveryResults.errors.push("USB not available");
      }
    } catch (error) {
      discoveryResults.errors.push(`USB discovery skipped: ${error.message}`);
    }

    // Discover network printers (simplified)
    try {
      discoveryResults.network = await this.discoverNetworkPrinters();
    } catch (error) {
      discoveryResults.errors.push(
        `Network discovery failed: ${error.message}`
      );
    }

    return {
      success: true,
      totalDevices:
        discoveryResults.usb.length + discoveryResults.network.length,
      ...discoveryResults,
    };
  }

  /**
   * Generate ESC/POS commands for kitchen order with printer profiles
   * @param {Object} orderData - Order data
   * @param {Object} printer - Printer configuration
   * @returns {Uint8Array} ESC/POS commands
   */
  generateKitchenOrderPrint(orderData, printer) {
    const profile = this.getPrinterProfile(
      printer.printer_brand || printer.brand
    );
    const paperWidth = parseInt(printer.paper_size) || 58;
    const maxWidth = Math.floor((paperWidth - 8) / 2); // Convert to characters (8 dots per character)

    let commands = [];

    // Helper function to convert string commands to Uint8Array
    const stringToBytes = (str) => {
      const encoder = new TextEncoder();
      return encoder.encode(str);
    };

    // Helper function to convert ESC/POS command arrays to Uint8Array
    const commandToBytes = (commandArray) => {
      return new Uint8Array(commandArray);
    };

    // Use printer profile commands
    commands.push(...commandToBytes(profile.init)); // Initialize printer

    // Center align for header
    commands.push(...commandToBytes(profile.alignCenter));

    // Restaurant name (bold)
    commands.push(...commandToBytes(profile.boldOn));
    commands.push(
      ...stringToBytes(
        this.centerText(orderData.restaurantName || "RESTAURANT", maxWidth) +
          "\n"
      )
    );
    commands.push(...commandToBytes(profile.boldOff));

    // Kitchen order header
    commands.push(
      ...stringToBytes(
        this.centerText("=== KITCHEN ORDER ===", maxWidth) + "\n\n"
      )
    );

    // Order info
    commands.push(...commandToBytes(profile.alignLeft));
    commands.push(...stringToBytes(`Table: ${orderData.tableName || "N/A"}\n`));
    commands.push(
      ...stringToBytes(`Order: ${orderData.orderNumber || "N/A"}\n`)
    );
    commands.push(...stringToBytes(`Time: ${new Date().toLocaleString()}\n`));
    commands.push(...stringToBytes(`Staff: ${orderData.staffName || "N/A"}\n`));
    commands.push(...stringToBytes("--------------------------------\n\n"));

    // Order items
    if (orderData.items && orderData.items.length > 0) {
      orderData.items.forEach((item, index) => {
        // Item name and quantity
        const itemLine = `${item.quantity}x ${item.name}`;
        commands.push(...stringToBytes(itemLine + "\n"));

        // Special notes/modifications
        if (item.notes || item.modifications) {
          commands.push(
            ...stringToBytes(`  Note: ${item.notes || item.modifications}\n`)
          );
        }

        // Add separator between items (except last)
        if (index < orderData.items.length - 1) {
          commands.push(...stringToBytes("--------------------------------\n"));
        }
      });
    }

    // Footer
    commands.push(...stringToBytes("\n\n================================\n"));
    commands.push(
      ...stringToBytes(this.centerText("END OF ORDER", maxWidth) + "\n")
    );

    // Cut paper
    commands.push(...commandToBytes(profile.cut));

    return new Uint8Array(commands);
  }

  /**
   * Generate ESC/POS commands for receipt with printer profiles
   * @param {Object} receiptData - Receipt data
   * @param {Object} printer - Printer configuration
   * @returns {Uint8Array} ESC/POS commands
   */
  generateReceiptPrint(receiptData, printer) {
    const profile = this.getPrinterProfile(
      printer.printer_brand || printer.brand
    );
    const paperWidth = parseInt(printer.paper_size) || 58;
    const maxWidth = Math.floor((paperWidth - 8) / 2); // Convert to characters

    let commands = [];

    // Helper function to convert string commands to Uint8Array
    const stringToBytes = (str) => {
      const encoder = new TextEncoder();
      return encoder.encode(str);
    };

    // Helper function to convert ESC/POS command arrays to Uint8Array
    const commandToBytes = (commandArray) => {
      return new Uint8Array(commandArray);
    };

    // Use printer profile commands
    commands.push(...commandToBytes(profile.init)); // Initialize printer

    // Center align for header
    commands.push(...commandToBytes(profile.alignCenter));

    // Restaurant name (bold)
    commands.push(...commandToBytes(profile.boldOn));
    commands.push(
      ...stringToBytes(
        this.centerText(receiptData.restaurantName || "RESTAURANT", maxWidth) +
          "\n"
      )
    );
    commands.push(...commandToBytes(profile.boldOff));

    // Receipt info
    commands.push(
      ...stringToBytes(this.centerText("RECEIPT", maxWidth) + "\n")
    );
    commands.push(
      ...stringToBytes(this.centerText("=================", maxWidth) + "\n\n")
    );

    // Order info
    commands.push(...commandToBytes(profile.alignLeft));
    commands.push(
      ...stringToBytes(`Table: ${receiptData.tableName || "N/A"}\n`)
    );
    commands.push(
      ...stringToBytes(`Receipt: ${receiptData.receiptNumber || "N/A"}\n`)
    );
    commands.push(
      ...stringToBytes(
        `Date: ${new Date(
          receiptData.timestamp || Date.now()
        ).toLocaleString()}\n`
      )
    );
    commands.push(
      ...stringToBytes(`Staff: ${receiptData.staffName || "N/A"}\n`)
    );
    if (
      receiptData.buffetTotalPersons > 0 &&
      receiptData.buffetPersonsLabel
    ) {
      commands.push(
        ...stringToBytes(
          `${receiptData.buffetPersonsLabel}: ${receiptData.buffetTotalPersons}\n`
        )
      );
    }
    commands.push(...stringToBytes("--------------------------------\n\n"));

    // Order items
    if (receiptData.items && receiptData.items.length > 0) {
      receiptData.items.forEach((item) => {
        const qty = item.quantity ?? item.qty ?? 1;
        const price = item.price ?? item.price_sale ?? 0;
        const itemLine = `${item.name} ${qty}x${price}`;
        commands.push(...stringToBytes(itemLine + "\n"));

        if (item.modifications) {
          commands.push(...stringToBytes(`  ${item.modifications}\n`));
        }
      });
    }

    // Buffet plan lines (priced covers — thermal ESC/POS)
    if (receiptData.buffetPlans && receiptData.buffetPlans.length > 0) {
      commands.push(...stringToBytes("\n--------------------------------\n"));
      commands.push(...commandToBytes(profile.boldOn));
      const buffetHeader =
        receiptData.buffetPlansHeader ||
        receiptData.buffet_plans_header ||
        "Buffet plans";
      commands.push(
        ...stringToBytes(
          this.centerText(String(buffetHeader).toUpperCase(), maxWidth) + "\n"
        )
      );
      commands.push(...commandToBytes(profile.boldOff));

      receiptData.buffetPlans.forEach((plan) => {
        const rawName = plan.name || "Plan";
        const name =
          rawName.length > maxWidth
            ? `${rawName.substring(0, Math.max(1, maxWidth - 3))}...`
            : rawName;
        commands.push(...stringToBytes(`${name}\n`));
        const unit = Number(plan.unitPrice ?? plan.unit_price ?? 0);
        const persons = Number(plan.persons ?? 0);
        let lineTotal = plan.subtotal;
        if (lineTotal == null || lineTotal === "") {
          lineTotal = persons * unit;
        }
        commands.push(
          ...stringToBytes(`  ${persons} x ${unit} = ${lineTotal}\n`)
        );
      });
    }

    // Total
    commands.push(...stringToBytes("\n--------------------------------\n"));
    commands.push(...commandToBytes(profile.boldOn));
    commands.push(...stringToBytes(`TOTAL: ${receiptData.total || "0.00"}\n`));
    commands.push(...commandToBytes(profile.boldOff));

    // Payment info
    if (receiptData.paymentMethod) {
      commands.push(
        ...stringToBytes(`Payment: ${receiptData.paymentMethod}\n`)
      );
    }

    if (receiptData.change) {
      commands.push(...stringToBytes(`Change: ${receiptData.change}\n`));
    }

    // Footer
    commands.push(...stringToBytes("\n\n================================\n"));
    commands.push(
      ...stringToBytes(
        this.centerText("Thank you for dining!", maxWidth) + "\n"
      )
    );

    // Cut paper
    commands.push(...commandToBytes(profile.cut));

    return new Uint8Array(commands);
  }

  /**
   * Helper function to center text
   * @param {string} text - Text to center
   * @param {number} maxWidth - Maximum width
   * @returns {string} Centered text
   */
  centerText(text, maxWidth) {
    const textLength = text.length;
    const padding = Math.max(0, Math.floor((maxWidth - textLength) / 2));
    return " ".repeat(padding) + text;
  }

  /**
   * Get printer profile by name
   * @param {string} profileName - Profile name
   * @returns {Object} Printer profile
   */
  getPrinterProfile(profileName) {
    const brandLower = (profileName || "generic").toLowerCase();
    return (
      this.printerProfiles.get(brandLower) ||
      this.printerProfiles.get("generic")
    );
  }
}

export default PrinterService;
